import React from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { ApiTableColumn } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationWithoutAccess } from '../models'
import {
  InstallationLink,
  InstallationWithoutAccessInformationMobile,
  InstallationWithoutAccessInstallerName,
  InstallationWithoutAccessOwnerName,
} from '../components/views/InstallationsWithoutAccess'
import InstallationWithoutAccessProductType from '../components/views/InstallationsWithoutAccess/InstallationWithoutAccessProductType'
import { format } from '@e3dc-react/shell/Libs/date'
import NoAccessToInstallationTooltip from '../components/views/InstallationsWithoutAccess/NoAccessToInstallationTooltip'

export interface InstallationWithoutAccessTableColumns extends InstallationWithoutAccess {
  createdBy: string
}

/*
 * This Hook provides table columns for an installation without access table
 * based on media breakpoints
 * Eg.:
 * const  tableColumns  = useInstallationWithoutAccessTableColumns()
 *
 *  <ApiView<InstallationsWithoutAccess>
      tableColumns={tableColumns}
      {...props}
    />
 */
export const useInstallationWithoutAccessTableColumns = (): ApiTableColumn<InstallationWithoutAccessTableColumns>[] => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))
  const { t } = useCustomTranslation()

  const tableColumnsAll: ApiTableColumn<InstallationWithoutAccessTableColumns>[] = [
    {
      name: 'id',
      label: t('installation.overview.installation'),
      value: (item: InstallationWithoutAccess) => {
        return <InstallationLink installation={item} />
      },
    },
    {
      name: 'permissionType',
      label: t('installerDashboard.installation-without-access-column'),
      value: (item: InstallationWithoutAccess) => {
        return <NoAccessToInstallationTooltip installation={item} />
      },
    },
    {
      name: 'deviceId',
      label: t('administration.installations.serialNumber'),
      value: (item: InstallationWithoutAccess) => {
        return item.deviceId
      },
    },
    {
      name: 'emsMasterProduct',
      label: t('installation.overview.productTyp'),
      value: (item: InstallationWithoutAccess) => {
        return <InstallationWithoutAccessProductType installation={item} />
      },
      sortable: false,
    },
    {
      name: 'ownerId',
      label: t('installation.overview.owner'),
      value: (item: InstallationWithoutAccess) => {
        return <InstallationWithoutAccessOwnerName installation={item} />
      },
      sortable: false,
    },
    {
      name: 'createdAt',
      label: t('installation.overview.createdAt'),
      value: (item: InstallationWithoutAccess) => format(new Date(item.createdAt), t('common.format.date-fns-date')),
    },
    {
      name: 'createdBy',
      label: t('installation.overview.installedBy'),
      value: (item: InstallationWithoutAccess) => {
        return <InstallationWithoutAccessInstallerName installation={item} />
      },
      sortable: false,
    },
  ]

  const tableColumnsSm: ApiTableColumn<InstallationWithoutAccessTableColumns>[] = [
    {
      name: 'id',
      label: t('installation.overview.installationInfo'),
      value: (item: InstallationWithoutAccess) => {
        return <InstallationWithoutAccessInformationMobile installation={item} />
      },
    },
  ]

  const tableColumnsLg = tableColumnsAll.filter(column => column.name !== 'deviceId')
  const tableColumnsMd = tableColumnsAll.filter(column => column.name !== 'deviceId' && column.name !== 'createdBy')

  if (downSm) return tableColumnsSm
  if (downMd) return tableColumnsMd
  if (downLg) return tableColumnsLg

  return tableColumnsAll
}
